//#region Imports and Global Definitions
import { useTheme } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { showMessage } from "react-native-flash-message";
import { ActivityIndicator, Card } from 'react-native-paper';
import CustomButton from '../../../../components/CustomButton';
import CustomDropDown from '../../../../components/CustomDropDown/CustomDropdown';
import CustomInput from '../../../../components/CustomInput';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import { useHRAdminEmployees } from '../../../../hooks/useHRAdminEmployees';
import { useSettingsContext } from '../../../../hooks/useSettingsContext';
import { DAYNAMES,MONTHNAMES } from '../../../../constants/FormatDate';
//#endregion 

const HEADINGS = [ 
   '','timescheme' ,...DAYNAMES
]
const MULTI_TIMESCHEME_HEADINGS = [ 
   'month','timescheme' ,...DAYNAMES
]


const HRAdminEmployeeTimeSchemes = ({
	employeeCompany,
	employeeID,
    setTimeschemes,
    selectedTimeschemes,

	location,
	branch,
	floor,

	setSelectedBranch,
	setSelectedLocation,
	setSelectedFloor,

	onSuccess,
	onCancel,
}) => {
	const { allowMultipleTimeschemes } = useSettingsContext();

	//#region Const Definitions
	const { width } = useWindowDimensions();
	const { colors } = useTheme();
	const styles = makeStyles(colors, width);
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);

	const [selectedBranch, setBranch] = useState(branch)
	const [selectedLocation, setLocation] = useState(location)
	const [selectedFloor, setFloor] = useState(floor)

	const [branches, setBranches] = useState([]);
	const [locations, setLocations] = useState([]);
	const [floors, setFloors] = useState([]);
	const [indexedTimeschemes, setIndexedTimeschemes] = useState([]); // monday - sunday
	const [timeschemeOptions, setTimeschemeOptions] = useState([]); // for dropdown
	// const [selectedTimeScheme, setSelectedTimeScheme] = useState(null);
	const { getDropdownValues, postTimeSchemes, getTimescheme } =
		useHRAdminEmployees();

	const months = React.useMemo(
		() =>
			MONTHNAMES.map((month, index) => ({
				month,
				mnth: index + 1,
				timeschemeId: null,
				monday: "",
				tuesday: "",
				wednesday: "",
				thursday: "",
				friday: "",
				saturday: "",
				sunday: "",
			})),
		[],
	);

	//#endregion
	//#region Language
	const { t, i18n } = useTranslation();
	//#endregion

	//#region UseEffect
	useEffect(() => {
		setIsLoading(true);
		prepareData();
		fetchBranches();
	}, []);

	useEffect(() => {
		if (!selectedBranch) return;
		setData(months);
		fetchLocations();
	}, [selectedBranch]);

	useEffect(() => {
		if (!selectedBranch || !selectedLocation) return;
		setData(months);
		fetchFloors();
		fetchTimeSchemes();
	}, [selectedBranch, selectedLocation]);

	//#endregion

	//#region Helpers
	async function prepareData() {
		if (!selectedTimeschemes) {
			setData(months);
			setIsLoading(false);
			return;
		}
        fetchTimeSchemes().then((indxTimeschemes)=>{
			 const data=Object.entries(selectedTimeschemes).map(([mnth,id]) => ({
					mnth: Number(mnth),
					timeschemeId: id,
					monday: indxTimeschemes[id].monday,
					tuesday: indxTimeschemes[id].tuesday,
					wednesday: indxTimeschemes[id].wednesday,
					thursday: indxTimeschemes[id].thursday,
					friday:  indxTimeschemes[id].friday,
					saturday:  indxTimeschemes[id].saturday,
					sunday: indxTimeschemes[id].sunday,
				}));
		setData(data);
		setIsLoading(false);
            } 
        )
		// {
		//     "employeeid": "1063730",
		//     "month": 12,
		//     "timescheme": "DTT",
		//     "description": "Nicosia (w)",
		//     "monday": 17,
		//     "tuesday": 17,
		//     "wednesday": 17,
		//     "thursday": 17,
		//     "friday": 12,
		//     "saturday": 0,
		//     "sunday": 0,
		//     "userid": "1063730",
		//     "emplTSUI": "f3df000c-75c4-4651-8c68-71bc2af774bd",
		//     "company": "DT",
		//     "branch": "NIC",
		//     "location": "NICDT",
		//     "floor": "1",
		//     "doc": "2024-08-06T16:54:00"
		// }

		// {
		//         month,
		//         mnth: index + 1,
		//         timescheme: undefined,
		//         monday: '',
		//         tuesday: '',
		//         wednesday: '',
		//         thursday: '',
		//         friday: '',
		//         saturday: '',
		//         sunday: ''
	}

	const hasEmptyTimeschemes = () => {
		return data.some((obj) => !obj.timeschemeId);
	};

	const handleSave = async () => {
		if (hasEmptyTimeschemes()) {
			showMessage({
				message: t("common:hasEmptyTimeschemesError"),
				type: "danger",
				duration: 3000,
			});
			return;
		}

        setTimeschemes(data.reduce((acc, d) => {
				acc[d.mnth] = d.timeschemeId;
				return acc;
			}, {})
        )

        setSelectedBranch(selectedBranch)
        setSelectedLocation(selectedLocation)
        setSelectedFloor(selectedFloor)
        onSuccess?.()

        return
	};
	//#endregion

	//#region Getters
	async function fetchBranches() {
		if (!employeeCompany) return;

		const sParams = [
			{ branch: { sFilt1: employeeCompany, sFilt2: "", sFilt3: "" } },
		];
		const branches = (await getDropdownValues(sParams))?.branch;
		const branchesOptionsTemp = [];
		branches.forEach((obj) => {
			branchesOptionsTemp.push({ label: obj.description, value: obj.branch });
		});
		setBranches(branchesOptionsTemp);
	}

	const fetchLocations = async () => {
		if (!employeeCompany || !selectedBranch) return;

		const sParams = [
			{
				location: {
					sFilt1: selectedBranch,
					sFilt2: employeeCompany,
					sFilt3: "",
				},
			},
		];
		const locations = (await getDropdownValues(sParams))?.location;
		const locationsTemp = [];
		locations.forEach((obj) => {
			locationsTemp.push({ label: obj.description, value: obj.location });
		});
		setLocations(locationsTemp);
	};

	const fetchFloors = async () => {
		if (!employeeCompany || !selectedBranch || !selectedLocation) return;

		const sParams = [
			{
				buildingFloor: {
					sFilt1: selectedBranch,
					sFilt2: employeeCompany,
					sFilt3: selectedLocation,
				},
			},
		];
		const floors = (await getDropdownValues(sParams))?.buildingFloor;
		const floorsTemp = [];
		floors.forEach((obj) => {
			floorsTemp.push({ label: obj.description, value: obj.floor });
		});
		setFloors(floorsTemp);
	};

	const fetchTimeSchemes = async () => {
		if (!employeeCompany || !selectedBranch || !selectedLocation) return;

		const sParams = [
			{
				timescheme: {
					sFilt1: employeeCompany,
					sFilt2: selectedBranch,
					sFilt3: selectedLocation,
				},
			},
		];
		const timeschemes = (await getDropdownValues(sParams))?.timescheme;
		const timeschemesTemp = [];
		timeschemes.forEach((obj) => {
			timeschemesTemp.push({ label: obj.description, value: obj.timescheme });
		});
		setTimeschemeOptions(timeschemesTemp);

		const indexedTimeschemes = timeschemes.reduce((acc, current) => {
			const key = current.timescheme;
			acc[key] = current;
			return acc;
		}, {});
		setIndexedTimeschemes(indexedTimeschemes);
        return indexedTimeschemes
	};
	//#endregion

	//#region Setters
	const setTimeScheme = (timeschemeId, index, item) => {
		const timescheme = indexedTimeschemes?.[timeschemeId];
		if (!timescheme) return;

		const updatedArray = data.map((item) => {
			// if index null change all timeschemes esle only timescheme with correct index
			if (index === null || item.mnth === index + 1) {
				return {
					...item,
					timeschemeId,
					monday: timescheme?.monday ?? "",
					tuesday: timescheme?.tuesday ?? "",
					wednesday: timescheme?.wednesday ?? "",
					thursday: timescheme?.thursday ?? "",
					friday: timescheme?.friday ?? "",
					saturday: timescheme?.saturday ?? "",
					sunday: timescheme?.sunday ?? "",
				};
			}
			return item;
		});
		setData(updatedArray);
	};
	//#endregion

	//#region Renderers
	//#endregion

	//#region Loading
	if (isLoading)
		return (
			<View style={styles.loader}>
				<ActivityIndicator animating={true} color={colors.blue} />
			</View>
		);
	//#endregion

	return (
		<View style={styles.mainView}>
			<View style={styles.rowView}>
				<View style={[styles.subMain]}>
					<View
						style={{
							flexDirection: "row",
							alignItems: "flex-start",
							marginBottom: "3%",
						}}
					>
						<View style={{ marginHorizontal: "2%" }}>
							<CustomLabel
								value={t(`common:branch`)}
								type="SUBTITLE_TIMESCHEMES"
							/>
							<CustomDropDown
								defValue={selectedBranch}
								options={branches}
								value={selectedBranch}
								type="TIMESCHEMES"
								setValue={setBranch}
							/>
						</View>
						<View style={{ marginHorizontal: "2%" }}>
							<CustomLabel
								value={t(`common:location`)}
								type="SUBTITLE_TIMESCHEMES"
							/>
							<CustomDropDown
								defValue={selectedLocation}
								options={locations}
								value={selectedLocation}
								type="TIMESCHEMES"
								setValue={setLocation}
							/>
						</View>
						<View style={{ marginHorizontal: "2%" }}>
							<CustomLabel
								value={t(`common:buildingFloor`)}
								type="SUBTITLE_TIMESCHEMES"
							/>
							<CustomDropDown
								defValue={selectedFloor}
								options={floors}
								value={selectedFloor}
								type="TIMESCHEMES"
								setValue={setFloor}
							/>
						</View>
						<View style={[styles.buttons, styles.functionalityButtons]}>
							<CustomButton
								isGradient
								iconName="checkmark"
								onPress={handleSave}
								type="BUTTON_SAVE"
								show="BUTTON_SAVE"
							/>

							{onCancel && (
								<CustomButton
									iconName="close"
									onPress={onCancel}
									type="BUTTON_CANCEL"
									show="BUTTON_CANCEL"
								/>
							)}
						</View>
					</View>
					<View>
						<View style={[styles.row, { marginRight: "3%" }]}>
							{data &&
								data.length > 0 &&
								(allowMultipleTimeschemes
									? MULTI_TIMESCHEME_HEADINGS
									: HEADINGS
								).map((name) => (
									<CustomInput
										key={name}
										value={t(`common:${name}`)}
										type="MAININPUT"
										editable={false}
									/>
								))}
						</View>
					</View>
					<View style={styles.innerView}>
						<View style={styles.functionsView}>
							<ScrollView style={styles.fullWidth}>
								{allowMultipleTimeschemes ? (
									data.map((monthObj, index) => (
										<Card
											key={monthObj.mnth}
											style={[styles.cardStyleFunctions]}
										>
											<Card.Content>
												<View style={styles.row}>
													<View
														style={[
															styles.item,
															{ justifyContent: "flex-start" },
														]}
													>
														<CustomLabel
															value={t(
																`common:${MONTHNAMES[monthObj.mnth - 1]}`,
															)}
															type={"DEPARTMENTS"}
														/>
													</View>
													<View style={[styles.item]}>
														<CustomDropDown
															defValue={monthObj.timeschemeId}
															options={timeschemeOptions ?? []}
															value={monthObj.timeschemeId}
															type="DEPARTMENTS"
															setValue={(e) =>
																setTimeScheme(e, index, monthObj)
															}
														/>
													</View>
													{DAYNAMES.map((dayname) => (
														<View key={dayname} style={[styles.item]}>
															<CustomInput
																value={monthObj[dayname] ?? ""}
																type="MAININPUT"
																editable={false}
															/>
														</View>
													))}
												</View>
											</Card.Content>
										</Card>
									))
								) : (
									<Card style={[styles.cardStyleFunctions]}>
										<Card.Content>
											<View style={styles.row}>
												<View
													style={[
														styles.item,
														{ justifyContent: "flex-start" },
													]}
												>
													<CustomLabel
														value={t(`common:timescheme`)}
														type={"DEPARTMENTS"}
													/>
												</View>
												<View style={[styles.item]}>
													<CustomDropDown
														defValue={data[0].timeschemeId}
														options={timeschemeOptions ?? []}
														value={data[0].timeschemeId}
														type="DEPARTMENTS"
														setValue={(e) => setTimeScheme(e, null, data[0])}
													/>
												</View>
												{DAYNAMES.map((dayname) => (
													<View key={dayname} style={[styles.item]}>
														<CustomInput
															value={data[0][dayname] ?? ""}
															type="MAININPUT"
															editable={false}
														/>
													</View>
												))}
											</View>
										</Card.Content>
									</Card>
								)}
							</ScrollView>
						</View>
					</View>
				</View>
			</View>
		</View>
	);
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
    },
    subMain: {
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        width: '100%'
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    cardStyleFunctions: {
        margin: 10,
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    item: {
        flex: 1,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    selectedButton: {
        margin: 10,
        borderRadius: 5,
        borderWidth: 2,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        borderColor: colors.buttons_submit,
    },
    backButton: {
        alignSelf: 'flex-end',
        margin: 0
    },
    innerView: {
        flex: 1,
    },
    functionsView: {
        flexDirection: 'row',
        height: '100%',
    },
    fullHeight: {
        height: '95%',
    },
    fullWidth: {
        height: '87%',
    },
    servLinesView: {
        width: '85%',
        height: '100%',
    },
    functionalityButtons: {
        justifyContent: 'flex-end'
    },
    buttons: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
        marginTop: '2%'
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
});
//#endregion

export default HRAdminEmployeeTimeSchemes;